import React, { useMemo, useEffect, useCallback } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { UserType } from "@/types/db/users";
import { ClientType } from "@/types/db/clients";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { Table, ColumnDef } from "@/components/NewTable";
import { useModal } from "@/hooks/useModal";
import { UserPicker } from "@/components/Pickers/UserPicker";
import { ModalCard } from "@/components/Modals/ModalCard";

interface PreferredUserType {
  user: {
    id: string;
    first_name?: string;
    last_name?: string;
    preferred_name?: string;
    email_address?: string;
  };
  reason?: string;
}

export interface RelatedPreferredUsersScreenProps {
  clientId: string;
  loading?: boolean;
  externalAddTrigger?: number;
}

export function RelatedPreferredUsersScreen({
  clientId,
  loading = false,
  externalAddTrigger,
}: RelatedPreferredUsersScreenProps) {
  const { colors } = useTheme();
  const { entityData: client, formData, updateFormData } = useEntityEdit<ClientType>();
  const { openModal, closeModal } = useModal();

  // Use formData if available, otherwise use entityData
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const preferredUsers: PreferredUserType[] =
    ((formData as any)?.client_preferred_users ?? (client as any)?.client_preferred_users) || [];

  const preferredUserIds = useMemo(() => preferredUsers.map((pu) => pu.user.id), [preferredUsers]);

  const handleAddPreferredUser = useCallback(
    (user: UserType) => {
      const newPreferredUser: PreferredUserType = {
        user: {
          id: user.id,
          first_name: user.first_name || undefined,
          last_name: user.last_name || undefined,
          preferred_name: user.preferred_name || undefined,
          email_address: user.email_address || undefined,
        },
        reason: "",
      };
      const updatedPreferredUsers = [...preferredUsers, newPreferredUser];
      updateFormData({ ...formData, client_preferred_users: updatedPreferredUsers } as any);
      closeModal();
    },
    [preferredUsers, formData, updateFormData, closeModal],
  );

  const openUserPicker = useCallback(() => {
    openModal(
      <ModalCard>
        <UserPicker
          title="Add Preferred User"
          excludeUserIds={preferredUserIds}
          onPick={handleAddPreferredUser}
          onCancel={closeModal}
        />
      </ModalCard>,
    );
  }, [openModal, closeModal, preferredUserIds, handleAddPreferredUser]);

  // Handle external add trigger from header button
  useEffect(() => {
    if (externalAddTrigger && externalAddTrigger > 0) {
      openUserPicker();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalAddTrigger]);

  const handleRemovePreferredUser = useCallback(
    (row: PreferredUserType) => {
      const updatedPreferredUsers = preferredUsers.filter((pu) => pu.user.id !== row.user.id);
      updateFormData({ ...formData, client_preferred_users: updatedPreferredUsers } as any);
    },
    [preferredUsers, formData, updateFormData],
  );

  const handleEdit = useCallback(
    (row: PreferredUserType, key: string, newValue: string) => {
      const updated = preferredUsers.map((pu) => (pu.user.id === row.user.id ? { ...pu, [key]: newValue } : pu));
      updateFormData({ ...formData, client_preferred_users: updated } as any);
    },
    [preferredUsers, formData, updateFormData],
  );

  const formatUserName = (user: PreferredUserType["user"]) => {
    if (!user) return "Unknown User";
    const firstName = user.preferred_name || user.first_name || "";
    const lastName = user.last_name || "";
    return `${firstName} ${lastName}`.trim() || "Unknown User";
  };

  // Define columns
  const columns: ColumnDef<PreferredUserType>[] = useMemo(
    () => [
      {
        key: "user",
        header: "User",
        flex: 1,
        minWidth: 150,
        accessor: (row) => formatUserName(row.user),
      },
      {
        key: "email",
        header: "Email",
        flex: 1,
        minWidth: 180,
        accessor: (row) => row.user?.email_address || "—",
      },
      {
        key: "reason",
        header: "Notes",
        flex: 1,
        minWidth: 200,
        editable: { type: "text", placeholder: "Notes / reason for preference" },
        accessor: (row) => row.reason || "",
      },
    ],
    [],
  );

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<PreferredUserType>
        data={preferredUsers}
        columns={columns}
        keyExtractor={(item) => item.user.id}
        onEdit={handleEdit}
        canEdit
        onDelete={handleRemovePreferredUser}
        canDelete
        emptyMessage="No preferred users"
        emptyIcon="account-star"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
